import React, { useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Container,
    CssBaseline,
    FormControlLabel,
    TextField,
    Typography,
    Card,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import logo from "../../Assets/Images/Logo 4.png";

// Use either local or production API URL
const APIURL = window.location.hostname === 'localhost' 
    ? "http://localhost:5400/auth" 
    : "https://crmb.apitrading.lk/auth";

export default function Login({ onLogin }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const [showSignup, setShowSignup] = useState(false);

    const handleLogin = async (event) => {
        event.preventDefault();
        setError(""); // Reset error before login attempt
      
        try {
          const response = await fetch(`${APIURL}/login`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include", // ✅ Allows cookies & sessions
            body: JSON.stringify({ email, password }),
          });
      
          if (!response.ok) {
            throw new Error(`HTTP Error: ${response.status}`);
          }
      
          const data = await response.json();
      
          if (data.success) {
            // ✅ Store user role and name in localStorage
            localStorage.setItem("userRole", data.role);
            localStorage.setItem("userName", data.name); // Store the user's name
            localStorage.setItem("sessionExpiry", new Date().getTime() + 24 * 60 * 60 * 1000); // Set session expiry time
      
            // ✅ Call onLogin to update React state
            onLogin(data.role, data.name, data.id);// Pass both role and name
      
            // ✅ Navigate AFTER updating state
            setTimeout(() => {
              if (data.role === "superadmin") {
                navigate("/carsSuperAdmin/createQuotation");
              } else if (data.role === "admin") {
                navigate("/carsAdmin/createQuotation");
              } else {
                navigate("/cars");
              }
            }, 300); // Small delay to ensure state is updated
          } else {
            setError("Invalid email or password.");
          }
        } catch (err) {
          console.error("Login Error:", err);
          setError(`Server error: ${err.message}`);
        }
      };
    
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100vh",
                textAlign: "center",
                position: "relative",
                backgroundColor: "rgba(0, 0, 0, 0.4)",
            }}
        >
            {/* Full-screen Title */}
            <Typography
                variant="h2"
                sx={{
                    position: "absolute",
                    top: "5%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    fontSize: "3rem",
                    fontWeight: "bold",
                    color: "white",
                    textShadow: "2px 2px 10px rgba(0, 0, 0, 0.9)",
                    fontFamily: ", serif",
                    display: { xs: 'none', md: 'block' }, // Hide on mobile view
                }}
            >
                <span style={{ color: "#F15A24" }}>API <span style={{ color: "#ccc" }}>TRADING</span></span>
            </Typography>

            {/* Subtitle */}
            <Typography
                variant="h4"
                sx={{
                    position: "absolute",
                    top: "12%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    color: "white",
                    display: { xs: 'none', md: 'block' }, // Hide on mobile view
                }}
            >
                User Management System
            </Typography>

            {/* Mobile Subtitle */}
            <Typography
                variant="subtitle1"
                sx={{
                    position: "absolute",
                    top: "1%",
                    left: "1%",
                    color: "white",
                    display: { xs: 'block', md: 'none' }, // Show on mobile view
                    fontSize: "0.9rem",
                    backgroundColor: "rgba(0, 0, 0, 0.4)", // Added black background color with opacity for backdrop effect
                    borderRadius: "4px", // Added border radius of 20
                    paddingLeft: "5px", // Added padding for better appearance
                    paddingRight: "5px",
                    backdropFilter: "blur(5px)", // Added backdrop blur effect
                }}
            >
                User Management System
            </Typography>

            {/* Mobile Title */}
            <Typography
                variant="h2"
                sx={{
                    position: "absolute",
                    top: "10%",
                    // left: "5%",
                    color: "white",
                    display: { xs: 'block', md: 'none' }, // Show on mobile view
                    fontSize: "1.8rem",
                    fontWeight: "bold",
                    textShadow: "2px 2px 10px rgba(0, 0, 0, 0.9)",
                    fontFamily: ", serif",
                }}
            >
                <span style={{ color: "#F15A24" }}>API <span style={{ color: "#ccc" }}>TRADING</span></span>
            </Typography>

            {/* Login Container */}
            <Container component="main" maxWidth="xs">
                <CssBaseline />
                <Card
                    sx={{
                        padding: 4,
                        borderRadius: 2,
                        backgroundColor: "rgba(112, 112, 112, 0.4)",
                        color: "white",
                        backdropFilter: "blur(15px)",
                        boxShadow: "0 8px 32px 0 rgba(135, 64, 31, 0.37)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                    }}
                >
                    <Typography variant="h5" sx={{ textAlign: "center" }}>SIGN IN</Typography>
                    <Box component="form" onSubmit={handleLogin} sx={{ mt: 2 }}>
                        <TextField
                            margin="normal"
                            fullWidth
                            label="Email"
                            variant="outlined"
                            sx={{
                                input: { color: "#bbb" },
                                label: { color: "#bbb" },
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": { borderColor: "#bbb" },
                                    "&:hover fieldset": { borderColor: "white" },
                                    "&.Mui-focused fieldset": { borderColor: "#F15A24" },
                                },
                                "& .MuiInputLabel-root": {
                                    color: "#bbb",
                                    "&.Mui-focused": {
                                        color: "#F15A24",
                                    },
                                },
                            }}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        <TextField
                            margin="normal"
                            fullWidth
                            label="Password"
                            type="password"
                            variant="outlined"
                            sx={{
                                input: { color: "#bbb" },
                                label: { color: "#bbb" },
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": { borderColor: "#bbb" },
                                    "&:hover fieldset": { borderColor: "white" },
                                    "&.Mui-focused fieldset": { borderColor: "#F15A24" },
                                },
                                "& .MuiInputLabel-root": {
                                    color: "#bbb",
                                    "&.Mui-focused": {
                                        color: "#F15A24",
                                    },
                                },
                            }}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        {error && <Typography style={{ marginTop: "10px" }} color="error">{error}</Typography>}
                        <Button type="submit" fullWidth variant="contained" sx={{ mt: 2, backgroundColor: "#F15A24" }}>
                            Sign in
                        </Button>
                        {showSignup && (
                            <Button
                                fullWidth
                                variant="outlined"
                                color="warning"
                                sx={{ mt: 2 }}
                                onClick={() => navigate("/signup")}
                            >
                                Sign Up as Super Admin
                            </Button>
                        )}
                    </Box>
                </Card>

                {/* Logo */}
                <Box component="img" src={logo} alt="Logo" sx={{ height: "60px", mt: 4 }} />
            </Container>
        </Box>
    );
}
